import { Box, Typography } from '@mui/material';
import { useState } from 'react';

import {
  CardFlair,
  CardFlairVariants,
  CreditsPill,
  DurationPill,
  EthicsPill,
  Skeleton,
} from '@/components';
import { ComplianceTypes, Routes } from '@/constants';
import { EVENT_CONSTANTS } from '@/services';
import { createRouteWithParams } from '@/utils';

import { Link } from '../../../Link';
import {
  EpisodeCardLargeDimensions,
  EpisodeCardVariants,
} from '../../constants';
import { episodeBannerStyles as styles } from '../../styles';
import { EpisodeBannerComponentProps } from '../../useEpisodeBanner';
import { BookmarkButton } from '../BookmarkButton';
import { PlayButton } from '../PlayButton';
import { PreviewButton } from '../PreviewButton';

interface EpisodeCardLargeProps extends EpisodeBannerComponentProps {
  onLinkClick?: () => void;
}

export const EpisodeCardLarge = ({
  episode,
  onLinkClick,
  isEpisodeEthics,
  impressionRef,
  referrer = {},
  compliance,
}: EpisodeCardLargeProps) => {
  const [coverLoaded, setCoverLoaded] = useState(false);

  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.EPISODE_CARD,
    variants: {
      ...referrer.variants,
      episodeCardVariant: EpisodeCardVariants.LARGE,
    },
  };

  const isCPE = compliance === ComplianceTypes.CPE;

  return (
    <Box
      ref={impressionRef}
      sx={[
        styles.root,
        {
          height: EpisodeCardLargeDimensions.height,
          width: EpisodeCardLargeDimensions.width,
        },
      ]}>
      <Box
        id="episode-cover-img"
        component="img"
        alt="episode cover"
        src={episode.cover}
        onLoad={() => setCoverLoaded(true)}
        sx={[styles.coverImage, { display: coverLoaded ? 'block' : 'none' }]}
      />
      {!coverLoaded && <Skeleton variant="rounded" />}
      <Link
        to={createRouteWithParams(Routes.EPISODE, {
          episodeId: episode.episodeId,
        })}
        onClick={onLinkClick}
        state={{ referrer: location }}
        color="inherit">
        <Box
          sx={[
            styles.gradientContainer,
            {
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0.65) 73%)',
              p: 2,
            },
          ]}>
          {!!episode.companyLogo && (
            <Box
              component="img"
              src={episode.companyLogo}
              alt="company logo"
              sx={[styles.companyLogo, { width: 54, height: 54 }]}
            />
          )}
          <Box sx={styles.cardBody}>
            <Box sx={[styles.textContainer, { width: '100%' }]}>
              <Box sx={styles.pillContainer}>
                {isCPE ? (
                  <CreditsPill credits={episode.credits} color="white" />
                ) : (
                  <DurationPill duration={episode.duration} color="white" />
                )}
                {isEpisodeEthics && !isCPE && <EthicsPill color="white" />}
              </Box>
              <Typography variant="h4" sx={styles.text}>
                {episode.name}
              </Typography>
            </Box>
            <Box sx={styles.buttons}>
              <PlayButton episode={episode} referrer={location} />
              <PreviewButton episode={episode} referrer={location} />
              <Box sx={styles.buttonsRight}>
                <BookmarkButton
                  episodeId={episode?.episodeId}
                  referrer={location}
                />
              </Box>
            </Box>
          </Box>
          {episode.episodeAgeTag === 'new' && (
            <CardFlair variant={CardFlairVariants.LARGE} />
          )}
        </Box>
      </Link>
    </Box>
  );
};
